import api from '../service/api.service';

import { parseQueryFilters } from '../../helpers/parseQueryFilters';
import { parseQuerySortFields } from '../../helpers/parseQuerySortFields';

import {
  type PaginationQueryParams,
  type Settlement,
  type SettlementPaymentDetails,
  type SettlementShort,
  type SettlementPaymentPreview,
  type SettlementTimeline,
  type SettlementResultSet,
  SettlementMemoResultSet,
  DashboardSettlementResultSet,
  type MemoDraftResponse,
  type PaginatedMemoDrafts,
  type ErrorData,
  type FilterTypes,
  type AccountingVendor,
  type CaseLedgerResponse,
} from '../../types';
import { AxiosError } from 'axios';
import type { UpdateAttorneyFeeDispositionRequest } from '../../views/PaymentsView/types';

const BASE_URL = 'v1/fo/settlements';

const accountingSystem = localStorage.getItem('accountingSystem');

interface CreateSettlementRequest {
  caseId: number;
  defendantId: number;
  settlementType: string;
  settledAmount: number;
  pipAmount?: number;
  lienReimbursementAmount?: number;
}

export type NetSuiteVendorSearchField =
  | 'name'
  | 'internalId'
  | 'payableAccountId'
  | 'payableAccountName'
  | 'taxId';

export type QuickBookVendorSearchField =
  | 'Id' | 'DisplayName' | 'CompanyName';

interface SearchNetSuiteVendorParams {
  field: NetSuiteVendorSearchField | QuickBookVendorSearchField;
  term: string;
  page?: number;
  size?: number;
}

export interface StopCheckTransactionRequest {
  netSettlementId: number;
  reason: string;
  additionalComments: string;
}

export interface StopDeductionTransactionRequest {
  deductionId: number;
  reason: string;
  additionalComments: string;
}

export interface RetryVendorPaymentWithUpdateRequest {
  deductionId: number;
  paymentMethod: string;
  vendorPaymentAccountId?: number | null;
}

export interface StopAttyFeeTransactionRequest {
  attyFeeId: number;
  reason: string;
  additionalComments: string;
}

export interface RetryAttyFeeVendorPaymentWithUpdateRequest {
  attyFeeId: number;
  vendorPaymentAccountId: number;
}

export const getSettlementTimeline = async (id: number) => {
  const targetUrl = `${BASE_URL}/${id}/timeline`;

  return await api.get<SettlementTimeline[]>(targetUrl);
};

export const getSettlement = async (id: number) => {
  const targetUrl = `${BASE_URL}/${id}`;

  return await api.get<Settlement>(targetUrl);
};

export const deleteSettlement = async (id: number) => {
  const targetUrl = `${BASE_URL}/${id}`;

  return await api.delete<void>(targetUrl);
};

export const getSettlements = async ({
  size,
  page,
  count,
  match,
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  const queryFilters = parseQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField);

  const targetUrl = `${BASE_URL}?size=${size}&page=${page}&count=${count}&match=${match || ''}${queryFilters || ''
    }${sortFilters}`;

  return await api.get<SettlementResultSet<SettlementShort>>(targetUrl);
};

export const getDasbhboardSettlements = async ({
  size,
  page,
  count,
  match,
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  const queryFilters = parseQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField);

  const targetUrl = `${BASE_URL}/dashboard?size=${size}&page=${page}&count=${count}&match=${match || ''
    }${queryFilters || ''}${sortFilters}`;

  return await api.get<DashboardSettlementResultSet<SettlementShort>>(targetUrl);
};

export const createSettlement = async (data: CreateSettlementRequest, file?: File) => {
  const formData = new FormData();

  // Add normal fields
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  // Add file
  if (file) {
    formData.append('file', file);
  }

  return await api.post<Settlement>(BASE_URL, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadPdfSettlement = async (
  file: File,
  caseId: number,
  settledAmount?: number,
  settlementType?: string,
) => {
  const formData = new FormData();
  caseId && formData.append('caseId', caseId.toString());
  settledAmount !== undefined && formData.append('settledAmount', settledAmount.toString());
  settlementType && formData.append('settlementType', settlementType.toUpperCase());
  formData.append('file', file);

  return await api.post<SettlementMemoResultSet>(`${BASE_URL}/upload-pdf`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
// /settlements/upload-pdf-preview-confirm

export const uploadPdfPreviewConfirm = async (
  file: File,
  data: SettlementMemoResultSet,
  defendantId?: number,
  settlementType?: string,
) => {
  const formData = new FormData();
  formData.append('file', file);

  if (data.caseId !== undefined) formData.append('caseId', String(data.caseId));
  if (data.settledAmount !== undefined)
    formData.append('settledAmount', String(data.settledAmount));
  if (data.pipAmount !== undefined) formData.append('pipAmount', String(data.pipAmount));
  if (settlementType || data.settlementType) {
    formData.append('settlementType', (settlementType || data.settlementType || '').toUpperCase());
  }
  if (data.attyFee !== undefined) formData.append('attyFee', String(data.attyFee));
  if (data.fileHash !== undefined) formData.append('fileHash', data.fileHash);
  if (data.attorneysFees !== undefined)
    formData.append('attorneysFees', JSON.stringify(data.attorneysFees));
  if (data.deductions !== undefined) formData.append('deductions', JSON.stringify(data.deductions));
  if (data.warningMessages !== undefined)
    formData.append('warningMessages', JSON.stringify(data.warningMessages));
  if (defendantId !== undefined) formData.append('defendantId', String(defendantId));

  // Backend returns FoPdfSettlementResponse structure
  return await api.post<Omit<MemoDraftResponse, 'draftId' | 'createdAt' | 'updatedAt'>>(
    `${BASE_URL}/upload-pdf-preview-confirm`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export const exportSettlements = async ({
  size,
  page,
  count,
  match,
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  const queryFilters = parseQueryFilters(filters);
  const sortFilters = parseQuerySortFields(activeSortField);

  const targetUrl = `${BASE_URL}/export?size=${size}&page=${page}&count=${count}&match=${match || ''
    }${queryFilters || ''}${sortFilters}`;

  return await api.post<string>(targetUrl);
};

export const getPaymentPreview = async (settlementId: number) => {
  // const targetUrl = `${BASE_URL}/${settlementId}/payment-preview`;
  const targetUrl = `${BASE_URL}/${settlementId}/payment-preview-v3`;

  return await api.get<SettlementPaymentPreview>(targetUrl);
};

export const getPaymentDetails = async (id: number) => {
  // const targetUrl = `${BASE_URL}/${id}/payment-details`;
  const targetUrl = `${BASE_URL}/${id}/payment-details-v3`;
  return await api.get<SettlementPaymentDetails>(targetUrl);
};

/**
 * Call this to start the settlement payment process. Acceptable settlement statuses are: Draft, Unpaid.
 */
export const paySettlement = async (settlementId: number, pin: string) => {
  const targetUrl = `${BASE_URL}/${settlementId}/pay`;

  return await api.put<void>(targetUrl, null, {
    headers: { 'x-pin': pin },
  });
};

export const releaseSettlement = async (id: number, pin: string) => {
  const targetUrl = `${BASE_URL}/${id}/release`;

  return await api.put<void>(targetUrl, null, {
    headers: { 'x-pin': pin },
  });
};

/**
 * Call this to restart the settlement payment process in case of a non-critical error.
 */
export const retrySettlementPayment = async (id: number, pin: string) => {
  const targetUrl = `${BASE_URL}/${id}/retry`;

  return await api.put<void>(targetUrl, null, {
    headers: { 'x-pin': pin },
  });
};

export const exportSettlementPaymentDetails = async (id: number) => {
  const targetUrl = `${BASE_URL}/${id}/export-details-excel`;

  return await api.get<ArrayBuffer>(targetUrl, {
    responseType: 'arraybuffer',
  });
};

/**
 * Call this to retry a specific vendor payment for a deduction.
 */
export const retryVendorPayment = async (deductionId: number) => {
  const targetUrl = `${BASE_URL}/deduction/retry-vendor-payment`;

  return await api.post<void>(targetUrl, {
    deductionId: deductionId,
  });
};

export const retryVendorPaymentWithUpdate = async (data: RetryVendorPaymentWithUpdateRequest) => {
  const targetUrl = `${BASE_URL}/deduction/retry-vendor-payment-with-update`;

  return await api.post<void>(targetUrl, data);
};

/**
 * Call this to retry a specific vendor payment for an attorney fee (VENDOR counsel only).
 */
export const retryAttyFeeVendorPayment = async (attyFeeId: number) => {
  const targetUrl = `${BASE_URL}/atty-fee/retry-vendor-payment`;

  return await api.post<void>(targetUrl, {
    attyFeeId: attyFeeId,
  });
};

/**
 * Saves the selected vendor payout account on the attorney fee, then retries the vendor payment.
 * The payment method is derived by the API from the account type (bank account → ACH, mailing address → CHECK).
 */
export const retryAttyFeeVendorPaymentWithUpdate = async (
  data: RetryAttyFeeVendorPaymentWithUpdateRequest,
) => {
  const targetUrl = `${BASE_URL}/atty-fee/retry-vendor-payment-with-update`;

  return await api.post<void>(targetUrl, data);
};

export const updateNetSettlementConfig = async (id: number, data: unknown) => {
  const targetUrl = `${BASE_URL}/${id}/config`;

  return await api.put<void>(targetUrl, data);
};

export const getDocumentSettlement = async (documentKey: string) => {
  const targetUrl = `${BASE_URL}/${documentKey}/document-url`;
  return await api.get(targetUrl);
};

export const updateSettlementDraft = async (data: unknown) => {
  const targetUrl = `${BASE_URL}/upload-pdf-confirm`;
  return await api.post<{ message: string; settlementId: number }>(targetUrl, data);
};

export interface UpdateSettlementAmountRequest {
  settledAmount: number;
  pipAmount?: number;
  lienReimbursementAmount?: number;
}

export const updateSettlementAmount = async (id: number, data: UpdateSettlementAmountRequest) => {
  const targetUrl = `${BASE_URL}/${id}/amount`;
  return await api.put<void>(targetUrl, data);
};

export const deleteDocumentSettlement = async (documentKey: string) => {
  const targetUrl = `${BASE_URL}/${documentKey}/document`;
  return await api.delete<{ message: string }>(targetUrl);
};

export const updateFirmDisposition = async (
  settlementId: number,
  payload: UpdateAttorneyFeeDispositionRequest,
) => {
  const targetUrl = `v2/cases/settlements/${settlementId}/attorney-fees/disposition`;
  return await api.put(targetUrl, payload);
};

/**
 * Builds the entity identifier part of the disposition request from a fee row.
 * Rows without counselEntityType are treated as firms (legacy shape).
 */
export const getAttorneyFeeEntityIdentifier = (row: {
  id?: number;
  counselEntityType?: 'FIRM' | 'VENDOR' | null;
  firmId?: number | null;
  vendorFirmParticipationId?: number | null;
}): Pick<UpdateAttorneyFeeDispositionRequest, 'counselEntityType' | 'firmId' | 'vendorFirmParticipationId'> =>
  row.counselEntityType === 'VENDOR'
    ? { counselEntityType: 'VENDOR', vendorFirmParticipationId: row.vendorFirmParticipationId ?? row.id }
    : { counselEntityType: 'FIRM', firmId: row.firmId ?? row.id };

export const searchNetSuiteVendor = async ({
  field,
  term,
  page = 1,
  size = 20,
}: SearchNetSuiteVendorParams) => {
  const targetUrl = `${BASE_URL}/accounting/${accountingSystem}/vendor-search`;
  return await api.get(targetUrl, {
    params: {
      field,
      term,
      page,
      size,
    },
  });
};

export const getNetSuiteVendor = async (internalId: string) => {
  const targetUrl = `${BASE_URL}/accounting/${accountingSystem}/vendor/${internalId}`;
  return await api.get(targetUrl);
};

export const retryClientExternalAccountPayment = async (netSettlementId: number) => {
  const targetUrl = `${BASE_URL}/retry-client-external-account-payment`;

  return await api.put<void>(targetUrl, {
    netSettlementId: netSettlementId,
  });
};

export const stopCheckTransaction = async (data: StopCheckTransactionRequest) => {
  const targetUrl = `${BASE_URL}/stop-check-transaction`;

  return await api.post<void>(targetUrl, data);
};

export const stopDeductionTransaction = async (data: StopDeductionTransactionRequest) => {
  const targetUrl = `${BASE_URL}/stop-deduction-transaction`;

  return await api.post<void>(targetUrl, data);
};

/**
 * Synchronous: resolves only once Passport reports the check as STOPPED.
 */
export const stopAttyFeeTransaction = async (data: StopAttyFeeTransactionRequest) => {
  const targetUrl = `${BASE_URL}/stop-atty-fee-transaction`;

  return await api.post<void>(targetUrl, data);
};

export const ApproveSettlement = async (settlementId: number, action: string, comment: string) => {
  const payload = {
    action: action,
    comment: comment,
  };
  const targetUrl = `${BASE_URL}/${settlementId}/approval-action`;
  return await api.put(targetUrl, payload);
};

export const postComment = async (settlementId: number, comment: string) => {
  const targetUrl = `${BASE_URL}/${settlementId}/approval-comment`;
  return await api.post(targetUrl, { comment: comment });
};

export const saveSettlement = async (settlementId: number, data: unknown) => {
  const targetUrl = `${BASE_URL}/${settlementId}/save`;
  return await api.put(targetUrl, data);
};

export const retryNetsuite = async (settlementId: number) => {
  const targetUrl = `${BASE_URL}/accounting-sync/${settlementId}/retry`;
  return await api.put(targetUrl);
};

// ============================================
// DRAFT MANAGEMENT API METHODS
// ============================================

/**
 * Error handler for draft API operations
 * Provides user-friendly error messages based on HTTP status codes
 */
export const handleDraftError = (error: AxiosError<ErrorData>): string => {
  const status = error.response?.status;
  const message = error.response?.data?.message;

  switch (status) {
    case 400:
      return message || 'Invalid request. Please check your data.';
    case 401:
      return 'Session expired. Please log in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'Draft not found or has been deleted.';
    case 409:
      return 'A draft already exists for this case. Load the existing draft or delete it first.';
    default:
      return message || 'An error occurred. Please try again.';
  }
};

/**
 * Save new settlement memo draft
 * POST /settlements/drafts
 * @param draftData - Settlement data in FoPdfSettlementResponse format (without draftId, createdAt, updatedAt)
 */
export const saveDraft = async (
  draftData: Omit<MemoDraftResponse, 'draftId' | 'createdAt' | 'updatedAt'>,
) => {
  const targetUrl = `${BASE_URL}/drafts`;
  return await api.post<MemoDraftResponse>(targetUrl, { draftData });
};

/**
 * Get draft by ID or case ID
 * GET /settlements/drafts?draftId=X or ?caseId=X
 *
 * @throws Error if both or neither parameter is provided
 */
export const getDraft = async (params: { draftId?: number; caseId?: number }) => {
  const { draftId, caseId } = params;

  if (!draftId && !caseId) {
    throw new Error('Either draftId or caseId must be provided');
  }
  if (draftId && caseId) {
    throw new Error('Only one of draftId or caseId should be provided');
  }

  const queryParam = draftId ? `draftId=${draftId}` : `caseId=${caseId}`;
  const targetUrl = `${BASE_URL}/drafts?${queryParam}`;
  return await api.get<MemoDraftResponse>(targetUrl);
};

/**
 * List all drafts with pagination and optional filters
 * GET /settlements/drafts?page=X&limit=Y&filter[caseId]=Z&filter[clientId]=A&filter[defendantId]=B&filter[date][from]=...&filter[date][to]=...
 */
export const listDrafts = async (page = 1, limit = 10, filters?: FilterTypes) => {
  const queryFilters = parseQueryFilters(filters);
  const targetUrl = `${BASE_URL}/drafts?page=${page}&limit=${limit}${queryFilters || ''}`;
  return await api.get<PaginatedMemoDrafts>(targetUrl);
};

/**
 * Update existing draft
 * PUT /settlements/drafts/{draftId}
 * @param draftData - Settlement data in FoPdfSettlementResponse format (without draftId, createdAt, updatedAt)
 */
export const updateDraft = async (
  draftId: number,
  draftData: Omit<MemoDraftResponse, 'draftId' | 'createdAt' | 'updatedAt'>,
) => {
  const targetUrl = `${BASE_URL}/drafts/${draftId}`;
  return await api.put<MemoDraftResponse>(targetUrl, { draftData });
};

/**
 * Delete draft (soft delete)
 * DELETE /settlements/drafts/{draftId}
 */
export const deleteDraft = async (draftId: number) => {
  const targetUrl = `${BASE_URL}/drafts/${draftId}`;
  return await api.delete<void>(targetUrl);
};

export const getCaseLedger = async (caseId: number | string, filters?: FilterTypes) => {
  const queryFilters = parseQueryFilters(filters);
  const targetUrl = `v1/fo/cases/${caseId}/ledger?${queryFilters || ''}`;
  return await api.get<CaseLedgerResponse>(targetUrl);
};

export const getAccountingVendor = async () => {
  const targetUrl = `${BASE_URL}/accounting/${accountingSystem}/vendor-form`;
  return await api.get<AccountingVendor>(targetUrl);
};

export const createQuickBooksVendor = async (data: unknown) => {
  const targetUrl = `${BASE_URL}/accounting/${accountingSystem}/vendor`;
  return await api.post<void>(targetUrl, data);
};